import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody,
  TableRow, TableCell, CircularProgress, Divider, Alert,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const fmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

function Row({ label, amount, bold, indent = 3, border }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: indent, border: 0, borderTop: border ? "1px solid" : 0, borderColor: "divider", py: 0.5, fontWeight: bold ? 700 : 400 }}>{label}</TableCell>
      <TableCell align="right" sx={{ border: 0, borderTop: border ? "1px solid" : 0, borderColor: "divider", py: 0.5, fontWeight: bold ? 700 : 400, fontVariantNumeric: "tabular-nums" }}>{fmt(amount)}</TableCell>
    </TableRow>
  );
}

export default function CashFlow() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!from || !to) { setError("Pick a date range"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/cash-flow", { params: { from, to } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Cash Flow"))
      .finally(() => setLoading(false));
  }, [from, to]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Cash Flow</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Actual cash in and out for the period (direct method) — reconciled to your counted cash register.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Cash Flow</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{data.fromDate} to {data.toDate}</Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ maxWidth: 620, mx: "auto" }}>
              <TableBody>
                <TableRow><TableCell colSpan={2} sx={{ border: 0, pt: 1, pb: 0.3, fontWeight: 700, fontSize: 15 }}>Operating cash (register)</TableCell></TableRow>
                <Row label="Cash received from customers" amount={data.operating.receipts} />
                <Row label="Cash paid to vendors" amount={-Math.abs(data.operating.vendorPayments)} />
                <Row label="Operating expenses paid" amount={-Math.abs(data.operating.expenses)} />
                <Row label="Net cash from operations" amount={data.operating.netChange} bold border />

                <TableRow><TableCell colSpan={2} sx={{ border: 0, pt: 2, pb: 0.3, fontWeight: 700, fontSize: 15 }}>Register reconciliation</TableCell></TableRow>
                <Row label="Beginning register balance" amount={data.beginningRegister} />
                <Row label="+ Net cash from operations" amount={data.operating.netChange} />
                <Row label="= Expected ending register" amount={data.computedEnding} bold border />
                <Row label="Actual counted register" amount={data.endingRegister} bold />
                <Row label="Checks / ACH / count adjustments" amount={data.nonDrawerOrAdjustments} border />
              </TableBody>
            </Table>
          </Box>

          {Math.abs(data.nonDrawerOrAdjustments) >= 0.005 && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 620, mx: "auto" }}>
              The counted register differs from operations by <b>{fmt(data.nonDrawerOrAdjustments)}</b>. That's normal —
              it's check/ACH money that went to the bank rather than the drawer, plus any manual count corrections.
            </Alert>
          )}

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Direct method from Daily Cash: receipts in, vendor payments and expenses out. Bank (check/ACH) money is
            tracked separately from the drawer, so it shows as the reconciling line rather than in operations.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
